import React from 'react';
import Award from 'lucide-react/dist/esm/icons/award.js';
import Calendar from 'lucide-react/dist/esm/icons/calendar.js';
import DollarSign from 'lucide-react/dist/esm/icons/dollar-sign.js';
import Eye from 'lucide-react/dist/esm/icons/eye.js';
import Gift from 'lucide-react/dist/esm/icons/gift.js';
import Grid3x3 from 'lucide-react/dist/esm/icons/grid-3x3.js';
import Heart from 'lucide-react/dist/esm/icons/heart.js';
import RefreshCw from 'lucide-react/dist/esm/icons/refresh-cw.js';
import Shield from 'lucide-react/dist/esm/icons/shield.js';
import Spade from 'lucide-react/dist/esm/icons/spade.js';
import Sparkles from 'lucide-react/dist/esm/icons/sparkles.js';
import Target from 'lucide-react/dist/esm/icons/target.js';
import TrendingUp from 'lucide-react/dist/esm/icons/trending-up.js';
import Trophy from 'lucide-react/dist/esm/icons/trophy.js';
import Users from 'lucide-react/dist/esm/icons/users.js';

const LucideIcons = {
  Award,
  Calendar,
  DollarSign,
  Eye,
  Gift,
  Grid3x3,
  Heart,
  RefreshCw,
  Shield,
  Spade,
  Sparkles,
  Target,
  TrendingUp,
  Trophy,
  Users,
};

/**
 * InfoCallouts - Reusable colored callout boxes with icons
 * 
 * @param {Object} props
 * @param {Array} props.callouts - Array of callout objects
 * @param {string} props.callouts[].icon - Lucide icon name (e.g., 'Calendar', 'RefreshCw', 'Trophy') or emoji
 * @param {string} props.callouts[].title - Callout title
 * @param {string} props.callouts[].description - Callout description text
 * @param {string} props.callouts[].bgColor - Background color theme (purple, amber, green, blue, orange, pink, red, cyan)
 * @param {string} props.callouts[].iconColor - Optional icon color override
 * @param {string} props.title - Optional section title
 * @param {string} props.layout - Layout style: 'horizontal' (row), 'vertical' (column), 'grid' (auto grid)
 * @param {string} props.className - Additional CSS classes
 */
export default function InfoCallouts({ 
  callouts = [], 
  title, 
  layout = 'horizontal', 
  className = '' 
}) {
  if (!callouts || callouts.length === 0) return null;

  // Color theme mappings for backgrounds and text
  const colorThemes = {
    purple: {
      bg: 'bg-purple-50 dark:bg-purple-900/20',
      border: 'border-purple-500',
      titleText: 'text-purple-900 dark:text-purple-100',
      descText: 'text-purple-800 dark:text-purple-200',
      iconColor: 'text-purple-600 dark:text-purple-400'
    },
    amber: {
      bg: 'bg-amber-50 dark:bg-amber-900/20',
      border: 'border-amber-500',
      titleText: 'text-amber-900 dark:text-amber-100',
      descText: 'text-amber-800 dark:text-amber-200',
      iconColor: 'text-amber-600 dark:text-amber-400'
    },
    green: {
      bg: 'bg-green-50 dark:bg-green-900/20',
      border: 'border-green-500',
      titleText: 'text-green-900 dark:text-green-100',
      descText: 'text-green-800 dark:text-green-200',
      iconColor: 'text-green-600 dark:text-green-400'
    },
    blue: {
      bg: 'bg-blue-50 dark:bg-blue-900/20',
      border: 'border-blue-500',
      titleText: 'text-blue-900 dark:text-blue-100',
      descText: 'text-blue-800 dark:text-blue-200',
      iconColor: 'text-blue-600 dark:text-blue-400'
    },
    orange: {
      bg: 'bg-orange-50 dark:bg-orange-900/20',
      border: 'border-orange-500',
      titleText: 'text-orange-900 dark:text-orange-100',
      descText: 'text-orange-800 dark:text-orange-200',
      iconColor: 'text-orange-600 dark:text-orange-400'
    },
    pink: {
      bg: 'bg-pink-50 dark:bg-pink-900/20',
      border: 'border-pink-500',
      titleText: 'text-pink-900 dark:text-pink-100',
      descText: 'text-pink-800 dark:text-pink-200',
      iconColor: 'text-pink-600 dark:text-pink-400'
    },
    red: {
      bg: 'bg-red-50 dark:bg-red-900/20',
      border: 'border-red-500',
      titleText: 'text-red-900 dark:text-red-100',
      descText: 'text-red-800 dark:text-red-200',
      iconColor: 'text-red-600 dark:text-red-400'
    },
    cyan: {
      bg: 'bg-cyan-50 dark:bg-cyan-900/20',
      border: 'border-cyan-500',
      titleText: 'text-cyan-900 dark:text-cyan-100',
      descText: 'text-cyan-800 dark:text-cyan-200',
      iconColor: 'text-cyan-600 dark:text-cyan-400'
    },
    indigo: {
      bg: 'bg-indigo-50 dark:bg-indigo-900/20',
      border: 'border-indigo-500',
      titleText: 'text-indigo-900 dark:text-indigo-100',
      descText: 'text-indigo-800 dark:text-indigo-200',
      iconColor: 'text-indigo-600 dark:text-indigo-400'
    }
  };

  // Layout class mappings
  const layoutClasses = {
    horizontal: 'flex flex-col sm:flex-row gap-4',
    vertical: 'flex flex-col gap-4',
    grid: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4'
  };

  // Check if icon is an emoji (simple check for emoji-like strings)
  const isEmoji = (icon) => {
    if (!icon || typeof icon !== 'string') return false;
    // Check if it's a short string that doesn't match common icon names
    return icon.length <= 4 && !/^[a-zA-Z]+$/.test(icon);
  };

  // Render icon - supports both Lucide icons and emojis
  const renderIcon = (icon, iconColor, theme) => {
    if (!icon) return null;

    if (isEmoji(icon)) {
      return <div className="text-2xl mb-2">{icon}</div>;
    }

    // Try to get the Lucide icon component
    const IconComponent = LucideIcons[icon];
    if (IconComponent) {
      return (
        <IconComponent 
          className={`w-6 h-6 mb-2 ${iconColor || theme.iconColor}`} 
        />
      );
    }

    // Fallback: render as emoji/text if icon name not found
    return <div className="text-2xl mb-2">{icon}</div>;
  };

  const renderCallout = (callout, index) => {
    const theme = colorThemes[callout.bgColor] || colorThemes.purple;
    
    return (
      <div
        key={index}
        className={`${theme.bg} border-l-4 ${theme.border} rounded-xl p-4 flex-1`}
      >
        {renderIcon(callout.icon, callout.iconColor, theme)}
        <h3 className={`font-bold ${theme.titleText} mb-1`}>
          {callout.title}
        </h3>
        <p className={`text-sm ${theme.descText}`}>
          {callout.description}
        </p>
      </div>
    );
  };

  return (
    <div className={className}>
      {title && (
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
          {title}
        </h2>
      )}
      <div className={layoutClasses[layout] || layoutClasses.horizontal}>
        {callouts.map((callout, index) => renderCallout(callout, index))}
      </div>
    </div>
  );
}
